import { Fragment, HTMLAttributes, useRef, useState } from 'react';
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Checkbox,
  Chip,
  Divider,
  FilterOptionsState,
  TextField,
  createFilterOptions,
} from '@mui/material';
import { MultiChipAutocompleteProps } from './MultiChipAutocomplete.types';

const SELECT_ALL_SENTINEL = '__select_all_sentinel__';

const defaultFilter = createFilterOptions<unknown>();

export function MultiChipAutocomplete<T>({
  options,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  getOptionDisabled,
  getChipIcon,
  maxItems,
  label,
  placeholder,
  size = 'small',
  id,
  renderOption,
  disableCloseOnSelect = true,
  renderTags: renderTagsProp,
  disableClearable = false,
  chipDeletable,
  selectAllLabel,
  showCheckboxes = false,
  maxVisibleTags,
}: MultiChipAutocompleteProps<T>) {
  const [inputValue, setInputValue] = useState('');
  // Tracks whether the next "reset" reason in onInputChange was triggered by the
  // user selecting an option (internal) vs. a parent re-render changing the value
  // reference (external). External resets must be ignored to keep typed text stable.
  const selectionJustHappenedRef = useRef(false);

  const isAtMaxCapacity = maxItems !== undefined && value.length >= maxItems;

  const isSentinel = (option: T): boolean => (option as unknown) === SELECT_ALL_SENTINEL;

  const isAllSelected = selectAllLabel !== undefined && value.length === options.length && options.length > 0;
  const isSomeSelected = selectAllLabel !== undefined && value.length > 0 && !isAllSelected;

  const augmentedOptions: T[] = selectAllLabel ? [SELECT_ALL_SENTINEL as unknown as T, ...options] : options;

  const computedGetOptionDisabled = (option: T): boolean => {
    if (isSentinel(option)) {
      return false;
    }
    if (getOptionDisabled?.(option)) {
      return true;
    }
    if (isAtMaxCapacity) {
      const isSelected = value.some((v) => (isOptionEqualToValue ? isOptionEqualToValue(option, v) : option === v));
      return !isSelected;
    }
    return false;
  };

  const computedGetOptionLabel = (option: T): string => {
    if (isSentinel(option)) {
      return selectAllLabel!;
    }
    return getOptionLabel(option);
  };

  const computedIsOptionEqualToValue = isOptionEqualToValue
    ? (option: T, val: T): boolean => {
        if (isSentinel(option) || isSentinel(val)) {
          return false;
        }
        return isOptionEqualToValue(option, val);
      }
    : undefined;

  const computedFilterOptions = selectAllLabel
    ? (opts: T[], params: FilterOptionsState<T>) => {
        const withoutSentinel = opts.filter((o) => !isSentinel(o));
        const filtered = defaultFilter(withoutSentinel as unknown[], params as FilterOptionsState<unknown>) as T[];
        return [SELECT_ALL_SENTINEL as unknown as T, ...filtered];
      }
    : undefined;

  const computedRenderOption = (
    props: HTMLAttributes<HTMLLIElement> & { key?: string },
    option: T,
    state: { selected: boolean }
  ) => {
    if (isSentinel(option)) {
      const { key, ...optionProps } = props;
      return (
        <Fragment key={key ?? SELECT_ALL_SENTINEL}>
          <li {...optionProps}>
            <Checkbox checked={isAllSelected} indeterminate={isSomeSelected} style={{ marginRight: 8 }} />
            {selectAllLabel}
          </li>
          <Divider component="li" />
        </Fragment>
      );
    }
    if (renderOption) {
      return renderOption(props, option, state);
    }
    const { key, ...rest } = props;
    return (
      <li key={key} {...rest}>
        {showCheckboxes && <Checkbox checked={state.selected} style={{ marginRight: 8 }} />}
        {getOptionLabel(option)}
      </li>
    );
  };

  const renderTags = (tagValue: T[], getTagProps: AutocompleteRenderGetTagProps) => {
    const visibleTags = maxVisibleTags !== undefined ? tagValue.slice(0, maxVisibleTags) : tagValue;
    const hiddenCount = maxVisibleTags !== undefined ? tagValue.length - maxVisibleTags : 0;
    return (
      <>
        {visibleTags.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const isDeletable = chipDeletable ? chipDeletable(option, tagValue) : !getOptionDisabled?.(option);
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              label={getOptionLabel(option)}
              icon={getChipIcon ? getChipIcon(option) : undefined}
              onDelete={isDeletable ? tagProps.onDelete : undefined}
            />
          );
        })}
        {hiddenCount > 0 && <Chip size="small" label={`+${hiddenCount}`} />}
      </>
    );
  };

  return (
    <Autocomplete
      id={id}
      multiple
      options={augmentedOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          setInputValue(newInputValue);
        } else if (reason === 'clear') {
          setInputValue('');
        } else if (reason === 'reset') {
          // Only clear when the reset was triggered by the user selecting an option,
          // not by a parent re-render that changed the value reference externally.
          if (selectionJustHappenedRef.current) {
            setInputValue('');
            selectionJustHappenedRef.current = false;
          }
        }
      }}
      onChange={(_, newValue) => {
        selectionJustHappenedRef.current = true;
        if (selectAllLabel && (newValue as unknown[]).includes(SELECT_ALL_SENTINEL)) {
          onChange(isAllSelected ? [] : [...options]);
          return;
        }
        const realNewValue = (newValue as T[]).filter((item) => !isSentinel(item));
        const permanent = getOptionDisabled ? value.filter((v) => getOptionDisabled(v)) : [];
        const merged = [
          ...permanent,
          ...realNewValue.filter(
            (item) => !permanent.some((p) => (isOptionEqualToValue ? isOptionEqualToValue(p, item) : p === item))
          ),
        ];
        onChange(merged);
      }}
      getOptionLabel={selectAllLabel ? computedGetOptionLabel : getOptionLabel}
      isOptionEqualToValue={selectAllLabel ? computedIsOptionEqualToValue : isOptionEqualToValue}
      getOptionDisabled={computedGetOptionDisabled}
      filterOptions={computedFilterOptions}
      disableCloseOnSelect={disableCloseOnSelect}
      disableClearable={disableClearable}
      renderTags={renderTagsProp ?? renderTags}
      renderOption={selectAllLabel || renderOption || showCheckboxes ? computedRenderOption : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : undefined}
          variant="outlined"
          size={size}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
}
